<template>
  <ProductDetailPage :class="showProductDetailPage ? 'visible' : 'invisible'" />
  <ConfiguratorOverlay :class="showConfigurator ? 'visible' : 'invisible'" />
  <ExtraOptionOverlay :class="showExtraOptionsPage ? 'visible' : 'invisible'" />
</template>

<script setup lang="ts">
import { useContinueToken } from '@/composables/UseContinueToken';
import { useExtraOptions } from '@/composables/UseExtraOptions';
import ConfiguratorOverlay from '@/pages/ProductPage/partials/Configurator/ConfiguratorOverlay.vue';
import ExtraOptionOverlay from '@/pages/ProductPage/partials/ExtraOptions/ExtraOptionOverlay.vue';
import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore';
import { useSizeStore } from '@/stores/Configurator/SizeStore';
import { useExtraOptionsStore } from '@/stores/ExtraOptionsStore';
import { ConfiguratorStep } from '@/types/Configurator/ConfiguratorStep';
import { promiseTimeout, useScrollLock } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref, Ref, watch } from 'vue';
import ProductDetailPage from './partials/ProductDetailPage/ProductDetailPage.vue';

const sizeStore = useSizeStore();
const extraOptions = useExtraOptions();
const extraOptionsStore = useExtraOptionsStore();
const configuratorStore = useConfiguratorStore();

useContinueToken().watchForChanges();

const showProductDetailPage = computed(
  () => Number(configuratorStore.currentStep) === ConfiguratorStep.ProductPage
);
const showConfigurator = computed(() =>
  [ConfiguratorStep.Configurator, ConfiguratorStep.HalfConfigurator].includes(
    Number(configuratorStore.currentStep)
  )
);
const showExtraOptionsPage = computed(
  () => Number(configuratorStore.currentStep) === ConfiguratorStep.ExtraOptions
);

const fullscreenStates = [
  ConfiguratorStep.Configurator,
  ConfiguratorStep.HalfConfigurator,
  ConfiguratorStep.ExtraOptions,
];
let page: HTMLBodyElement;

const mediaQuery = window.matchMedia('(max-width: 767px)');

const screenSizeCheck = () => {
  configuratorStore.isMobile = mediaQuery.matches;
};

const browserNavigationHandler = (() => {
  let isHandling = false;

  return (event: Event) => {
    if (isHandling) {
      return;
    }
    isHandling = true;

    event.preventDefault();

    // Set configurator step
    if (showExtraOptionsPage.value) {
      configuratorStore.setConfiguratorStep();
      isHandling = false;
      return;
    }

    // Set product page
    if (showConfigurator.value) {
      configuratorStore.currentStep = ConfiguratorStep.ProductPage;
      isHandling = false;
      return;
    }

    // Go back in history
    if (showExtraOptionsPage.value) {
      window.history.back();
    }

    isHandling = false;
  };
})();

const bodyElement: Ref<HTMLElement> = ref(document.body);
const isBackgroundScrollLocked: Ref<boolean> = useScrollLock(bodyElement);

function retrieveInitialExtraOptionCategories(): void {
  extraOptions
    .requestCategories(sizeStore.selectedSizeValue.attributes.sku!)
    .then(extraOptionsStore.selectDefaultProductOptions);
}

onMounted(() => {
  // Check if user reloaded page, when on configurator, reset data
  const navigation = performance.getEntriesByType('navigation');
  if (
    navigation.length > 0 &&
    // @ts-ignore TS2339
    navigation[0].type === 'reload' &&
    configuratorStore.currentStep === ConfiguratorStep.Configurator
  ) {
    configuratorStore.resetData();
  }

  const pageWrapper = document.querySelector('#html-body') as HTMLBodyElement;

  if (!pageWrapper) {
    throw new Error("Can't find page wrapper, something is very wrong");
  }

  page = pageWrapper;

  if (fullscreenStates.includes(configuratorStore.currentStep)) {
    hideProductPage();
  }

  // Initial screen size check
  screenSizeCheck();

  // Retrieve initial extra option categories to determine flow
  retrieveInitialExtraOptionCategories();

  // Add event listener to check if screen size changes
  mediaQuery.addEventListener('change', screenSizeCheck);

  window.history.replaceState(null, '', window.location.href);
  window.addEventListener('popstate', browserNavigationHandler);
});

onUnmounted(() => {
  // Cleanup event listeners
  mediaQuery.removeEventListener('change', screenSizeCheck);

  window.removeEventListener('popstate', browserNavigationHandler);
});

watch(
  () => configuratorStore.currentStep,
  (newOpen, oldOpen) => {
    const wasFullscreen = fullscreenStates.includes(oldOpen);
    const isFullscreen = fullscreenStates.includes(newOpen);

    // If we go from configurator to product page, reset data (this fixes an issue with the exit warning in theme selector)
    if (
      newOpen === ConfiguratorStep.ProductPage &&
      oldOpen === ConfiguratorStep.Configurator
    ) {
      configuratorStore.resetData();
    }

    // Handle state management:
    if (newOpen > oldOpen) {
      window.history.pushState(null, '', window.location.href);
    }

    // If configurator opened
    if (wasFullscreen && isFullscreen) {
      // Do nothing if we stay fullscreen
      return;
    }

    if (!page) {
      return;
    }

    if (isFullscreen) {
      hideProductPage();
      return;
    }

    showProductPage();
  }
);

async function hideProductPage() {
  // Wait 100 ms for the rest of the page to be loaded
  await promiseTimeout(100);
  isBackgroundScrollLocked.value = true;
  page.style.overflowY = 'hidden';
  page.style.touchAction = 'none';
  page.style.overscrollBehavior = 'none';
  page.style.position = 'fixed';
}

function showProductPage() {
  isBackgroundScrollLocked.value = false;
  page.style.overflowY = 'scroll';
  page.style.touchAction = 'auto';
  page.style.overscrollBehavior = 'auto';
  page.style.position = 'initial';
}
</script>
